import { Alert, Form, message, Modal, Select, Typography } from 'antd';
import { Permission } from '@aid/shared';
import type { SolicitudResponse } from '../../../api/types';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { useRolesList } from '../../roles/api/useRoles';
import { useApproveSolicitud } from '../api/useSolicitudes';

interface ApproveSolicitudModalProps {
  solicitudId: number;
  open: boolean;
  onClose: () => void;
  solicitud?: SolicitudResponse;
}

interface ApproveFormValues {
  roleIds?: string[];
}

export function ApproveSolicitudModal({
  solicitudId,
  open,
  onClose,
  solicitud,
}: ApproveSolicitudModalProps) {
  const t = useT();
  const [form] = Form.useForm<ApproveFormValues>();
  const canReadRoles = useHasPermission(Permission.ROLES_READ);
  const { data: rolesData, isLoading: rolesLoading } = useRolesList(
    { limit: 100, isActive: true },
    { enabled: canReadRoles },
  );
  const approve = useApproveSolicitud();

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = async (values: ApproveFormValues) => {
    try {
      await approve.mutateAsync({ id: solicitudId, roleIds: values.roleIds ?? [] });
      message.success(t('pages.solicitudes.approveSuccess'));
      handleCancel();
    } catch {
      message.error(t('pages.solicitudes.approveError'));
    }
  };

  return (
    <Modal
      title={t('pages.solicitudes.approveTitle')}
      open={open}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      confirmLoading={approve.isPending}
      destroyOnHidden
      okText={t('pages.solicitudes.approve')}
      cancelText={t('common.cancel')}
      centered
      width={520}
      afterOpenChange={(nextOpen) => {
        if (!nextOpen) form.resetFields();
      }}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      {solicitud && (
        <Typography.Paragraph type="secondary">
          {solicitud.email} · {solicitud.keycloakId}
        </Typography.Paragraph>
      )}
      <Form<ApproveFormValues>
        form={form}
        layout="vertical"
        preserve={false}
        clearOnDestroy
        onFinish={handleFinish}
      >
        {canReadRoles ? (
          <Form.Item
            name="roleIds"
            label={t('pages.solicitudes.roleLabel')}
            rules={[{ required: true, message: t('pages.solicitudes.roleRequired') }]}
          >
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder={t('pages.solicitudes.rolePlaceholder')}
              loading={rolesLoading}
              options={(rolesData?.data ?? [])
                .filter((role) => role.isActive)
                .map((role) => ({
                  label: role.name,
                  value: role.id,
                }))}
            />
          </Form.Item>
        ) : (
          <Alert
            type="info"
            showIcon
            title={t('pages.solicitudes.rolesUnavailableTitle')}
            description={t('pages.solicitudes.rolesUnavailableDescription')}
          />
        )}
      </Form>
    </Modal>
  );
}
